import React from "react";
import { Button } from "@material-ui/core";   // ✅ import Button
import "./CapabilityMatrix.css";

const toTitleCase = (str) =>
    str ? str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase()) : "";

const CapabilityMatrix = ({ data, actions = [] }) => {
    if (!data || data.length === 0) return <p>No data</p>;

    // ✅ Normalize text case
    const normalized = data.map((d) => ({
        ...d,
        PhaseName: toTitleCase(d.PhaseName),
        FunctionName: toTitleCase(d.FunctionName),
    }));

    const phases = [...new Set(normalized.map((d) => d.PhaseName))];
    const functions = [...new Set(normalized.map((d) => d.FunctionName))];

    const hasData = normalized.length > 0; // <-- derive hasData

    return (
        <div className="matrix-wrapper">
            {/* ✅ Legend */}
            <div className="legend">
                <div className="legend-item">
                    <span className="legend-box full"></span> Full Completion
                </div>
                <div className="legend-item">
                    <span className="legend-box partial"></span> Partial Completion
                </div>
                <div className="legend-item">
                    <span className="legend-box none"></span> Not Started
                </div>
                <div className="legend-item">
                    <span className="legend-box blank"></span> No Data
                </div>
            </div>

            {/* ✅ Matrix Table */}
            <table className="matrix">
                <thead>
                    <tr>
                        <th>Phase</th>
                        {functions.map((fn) => (
                            <th key={fn}>{fn}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {phases.map((phase) => (
                        <tr key={phase}>
                            <td className="y-axis">{phase}</td>
                            {functions.map((fn) => {
                                const match = normalized.find(
                                    (d) => d.FunctionName === fn && d.PhaseName === phase
                                );
                                if (!match) return <td key={fn} className="blank"></td>;

                                const { ScoredCapabilities, TotalCapabilities } = match;

                                let bgGradient =
                                    "linear-gradient(90deg, #f5f5f5 0%, #fafafa 100%)"; // default blank
                                if (ScoredCapabilities === TotalCapabilities && TotalCapabilities > 0) {
                                    bgGradient =
                                        "linear-gradient(90deg, rgba(42,155,59,1) 0%, rgba(129,237,83,1) 100%)"; // green
                                } else if (ScoredCapabilities > 0) {
                                    bgGradient =
                                        "linear-gradient(90deg, rgba(255,193,7,1) 0%, rgba(255,238,88,1) 100%)"; // yellow
                                } else if (ScoredCapabilities === 0) {
                                    bgGradient =
                                        "linear-gradient(90deg, rgba(244,67,54,1) 0%, rgba(255,138,128,1) 100%)"; // red
                                }

                                return (
                                    <td key={fn} className="cell">
                                        <div
                                            style={{
                                                width: "100%",
                                                height: "100%",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                background: bgGradient,
                                                fontWeight: 600,
                                                borderRadius: "10px",
                                                fontSize: "0.85rem",
                                                color:
                                                    ScoredCapabilities === TotalCapabilities && TotalCapabilities > 0
                                                        ? "#fff"
                                                        : "#000",
                                            }}
                                        >
                                            {ScoredCapabilities}/{TotalCapabilities}
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>

            {/* ✅ Action buttons (below matrix) */}
            {hasData && actions.length > 0 && (
                <div
                    style={{
                        display: "flex",
                        justifyContent: "flex-start",
                        margin: "10px",
                    }}
                >
                    {actions.map((action, i) => (
                        <Button
                            key={i}
                            variant="outlined"
                            color="primary"
                            size="small"
                            style={{
                                borderRadius: "20px",
                                textTransform: "none",
                                marginRight: "8px",
                            }}
                            onClick={action.onClick}
                        >
                            {action.label}
                        </Button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default CapabilityMatrix;
